import React, {useContext, useEffect, useReducer, useState} from "react";
import {Button, message} from "antd";
import BuildingProperties from "./BuildingProperties";
import {useCurrentUser} from "../../../../contexts/UserContext";
import PrimaryRoofAttributes from "./PrimaryRoofAttributes";
import AdditionalRoofAttributes from "./AdditionalRoofAttributes";
import {useViewerStore} from "../../../../store/store";
import {FiInbox} from "react-icons/fi";
import {BiReset} from 'react-icons/bi';
import siteService from "../../../../services/siteService";
import {ThemeContext} from "../../../../contexts/ThemeContext";
import {Splide, SplideSlide} from "@splidejs/react-splide";
import {logger} from "../../../../services/loggingService";
import {Toggle} from '../../../../components';
import cloneDeep from 'lodash/cloneDeep';
import { BuildingDesignData, BuildingInfo, DesignData, MappedData, RoofDesignData, RoofInfo, RoofMesh } from "../../../../@types/types";
import { MessageInstance } from "antd/es/message/interface";
import { AlertType } from "../../../../@types/enums";

export const BuilderAtributesTab: React.FC = () => {
    const sceneViewer = useViewerStore(state => state.sceneViewer);

    const {theme} = useContext(ThemeContext);
    const [messageApi, contextHolder] = message.useMessage();
    const { pathname, siteDesignDetails, buildingListData, isBuildingChange, isRoofChange, isResetEdata, designList, isRoofTagVisible, selectedDesignIndex } = useViewerStore();
    const [selectedBuilding, setSelectedBuilding] = useState<BuildingDesignData | undefined>(undefined);
    const [selectedBuildingIndex, setSelectedBuildingIndex] = useState(-1);
    const [designData, setDesignData] = useState<DesignData[]>([]);
    const [saveLoading, setSaveLoading] = useState(false);
    const {user, hasCapability} = useCurrentUser();
    const [readOnly, setReadonly] = useState(false);
    const [showInactiveRoofs, toggleShowInactiveRoofs] = useReducer(state => !state, false);
    const [showAdditionalRoofsProperties, toggleShowAdditionalRoofs] = useReducer(state => !state, false);

    const resetSelectedBuilding = async () => {
        if (selectedDesignIndex !== -1 && designList.length > 0) {
            setDesignData(cloneDeep(designList));
            let updatedRoofs = cloneDeep(designList[0].Buildings[0].Roofs);

            setSelectedBuilding({
                ...designList[0].Buildings[0],
                Roofs: updatedRoofs 
            });
            setSelectedBuildingIndex(0);
            useViewerStore.setState({isBuildingChange: true});
        }
    };

    useEffect(() => {
        resetSelectedBuilding();
    }, [pathname, siteDesignDetails, designList, selectedDesignIndex]);

    useEffect(() => {
        if (isResetEdata) {
            resetSelectedBuilding();
            useViewerStore.setState({
                isResetEdata: false,
                isRoofChange: false,
            });
        }
    }, [isResetEdata]);

    useEffect(() => {
        if (user?.user_Id) {
            setReadonly(!hasCapability("Edit_Building_Attributes"));
        }
    }, [user]);

    useEffect (()=>{
        const opId = sessionStorage.getItem('SalesForceId') || '';
        useViewerStore.setState({ isloadingOldDesignList: true });
        siteService.GetOldBuildingAttributes(opId).then(response => {
            useViewerStore.setState({ oldDesignList: response?.Designs || [] });
        }).finally(() => {
            useViewerStore.setState({ isloadingOldDesignList: false });
        });
    }, [designList?.length]);

    const selectBuilding = (item: BuildingDesignData, index: number) => {
        if (index !== selectedBuildingIndex && selectedBuilding) {
            let newDesignData = cloneDeep(designData);
            newDesignData[0].Buildings[selectedBuildingIndex] =
                selectedBuilding;
            setDesignData(newDesignData);
            setSelectedBuilding(item);
            setSelectedBuildingIndex(index);
        }
    };
    const saveBuildingHandler = ()=> {
        if(!selectedBuilding) return;
        setSaveLoading(true);
        let newDesignData = cloneDeep(designData);
        newDesignData[0].Buildings[selectedBuildingIndex] =
            selectedBuilding;
        saveBuildingData(newDesignData, (success) => {
            setSaveLoading(false);
            if (success) {
                setDesignData(newDesignData);
                setSelectedBuilding(newDesignData[0].Buildings[selectedBuildingIndex]);
            }
        }, messageApi);
    }

    const handleCopyOldRoofAttributes = (index: number, roofData: RoofDesignData | null) => {
        
        if (!roofData || !selectedBuilding){ 
            sceneViewer.viewerAlert({
                show: true,
                title: "Copy Old Roof Attributes",
                message: "No old roof attributes found",
                messageType: AlertType.error,
            });
            return;
        }
        const updatedRoofs = cloneDeep(selectedBuilding.Roofs);
        updatedRoofs[index] = roofData;
        
        setSelectedBuilding((prev: BuildingDesignData | undefined) => 
                (prev ? { ...prev, Roofs: [...updatedRoofs]} : prev)
        );
        useViewerStore.setState({ isRoofChange: true });
        sceneViewer.viewerAlert({
            show: true,
            title: "Copy Old Roof Attributes",
            message: "Old Roof Attributes copied successfully",
            messageType: AlertType.success,
        });
    }

    useEffect(() => {
        if (selectedBuildingIndex > -1 && selectedBuilding) {
            let newBlData = buildingListData;
            newBlData[selectedBuildingIndex] = selectedBuilding;
            const isDesignChange = JSON.stringify(designData) !== JSON.stringify(designList);
            const isBuildingChange = isDesignChange || JSON.stringify(selectedBuilding) !== JSON.stringify(designList[0].Buildings[selectedBuildingIndex]);
            useViewerStore.setState({buildingListData: newBlData, isBuildingChange});
        }
    }, [selectedBuilding]);

    const toggleRoofTag = () => {
        sceneViewer.toggleRoofTag(!isRoofTagVisible);
        useViewerStore.setState({isRoofTagVisible: !isRoofTagVisible});
    }

    return (
        <>
            {contextHolder}
            {designData.length > 0 && selectedBuilding && selectedDesignIndex !== -1 ? (
                <div className="tab__inner" style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    <div>
                        <div className="tab__title">
                            <span className="tab__title--text">Building Attributes</span>
                            {isBuildingChange && <div className="tab__title--btns">
                                <button
                                    onClick={resetSelectedBuilding}
                                    className='btn btn-clear btn-resize'
                                    title={'Reset to Default'}>
                                    <BiReset />
                                </button>
                            </div>}
                        </div>
                        <div className="tab__column building-carousel-column">
                            <div className="building-carousel">
                            <Splide
                                options={{
                                    rewind: false,
                                    width: "100%",
                                    gap: "10px",
                                    perPage: 2,
                                    perMove: 1,
                                    padding: "22px",
                                    arrows: true,
                                }}>
                                {designData.length > 0 &&
                                    designData[0].Buildings.map((item, index) => (
                                        <SplideSlide key={index}>
                                            <Button
                                                id="buildingSwitchBtn"
                                                type={
                                                    selectedBuilding && selectedBuilding.Id === item.Id
                                                        ? "primary"
                                                        : "default"
                                                }
                                                size="middle"
                                                onClick={() => selectBuilding(item, index)}
                                                key={index}
                                                block
                                            >
                                                Building {index + 1}
                                            </Button>
                                        </SplideSlide>
                                    ))}
                            </Splide>
                            </div>
                        </div>
                    </div>
                    <div style={{overflowY: 'auto'}}>
                        <div className='tab__flex justify-between' style={{margin: "10px"}}>
                            <span className='tab__column--subtitle'>Show/Hide Roof Tag</span>
                            <Toggle
                                on={isRoofTagVisible}
                                onChange={toggleRoofTag}
                            />
                        </div>

                        <div className='tab__flex justify-between' style={{margin: "10px"}}>
                            <span className='tab__column--subtitle'>Display Inactive Roofs</span>
                            <Toggle
                                on={showInactiveRoofs}
                                onChange={toggleShowInactiveRoofs}
                            />
                        </div>

                        <div className='tab__flex justify-between' style={{ margin: "10px" }}>
                            <span className='tab__column--subtitle'>Additional Roof Notes</span>
                            <Toggle
                                on={showAdditionalRoofsProperties}
                                onChange={toggleShowAdditionalRoofs}
                            />
                        </div>

                        <BuildingProperties
                            selectedBuilding={selectedBuilding}
                            setSelectedBuilding={setSelectedBuilding}
                            readOnly={readOnly}
                        />
                        {(!showAdditionalRoofsProperties ?
                            <PrimaryRoofAttributes
                                selectedBuilding={selectedBuilding}
                                setSelectedBuilding={setSelectedBuilding}
                                handleCopyOldRoofAttributes={handleCopyOldRoofAttributes}
                                showInactive={showInactiveRoofs}
                                readOnly={readOnly}
                            /> :
                            <AdditionalRoofAttributes
                                selectedBuilding={selectedBuilding}
                                setSelectedBuilding={setSelectedBuilding}
                                handleCopyOldRoofAttributes={handleCopyOldRoofAttributes}
                                showInactive={showInactiveRoofs}
                                readOnly={readOnly}
                            />
                        )}
                    </div>
                    {(hasCapability("Edit_Building_Attributes") ||
                        hasCapability("Toggle_Engg_Letter")) && (
                            <div className="tab__column obst-edit-mask">
                                <Button
                                    id="buildingSwitchBtn"
                                    type="primary"
                                    size="middle"
                                    className="active"
                                    block
                                    onClick={saveBuildingHandler}
                                    loading={saveLoading}
                                >
                                    Save
                                </Button>
                            </div>
                        )}
                </div>
            ) : (
                <div className="empty_tab">
                    <>
                        <FiInbox
                            size={50}
                            color={theme === "dark" ? "#ffffff" : "#000000"}
                        />
                        <br/>
                        <p className={"theme-based-text-color"}>
                            The Roof and Building Data isn't available for {selectedDesignIndex === -1 ? 'original site' : 'this customer'}.
                            Please finalize the site to generate the data.
                        </p>
                    </>
                </div>
            )}
        </>
    );
};


export const saveBuildingData = (newDesignData: DesignData[], callback?: (success: boolean) => void, messageApi?: MessageInstance) => {
    const { sceneViewer, masterDataList, refreshQuestionableRoof, siteDesignDetails, deactivatedRoofIdArr } = useViewerStore.getState();
    const alertMessage = (type: string, content: string) => {
        if (!messageApi) return;
        messageApi.open({
            content: content,
            className:
                type === "error"
                    ? "alertError"
                    : type === "success"
                        ? "alertSuccess"
                        : "",
        });
    };

    const mappedData: MappedData = {
        site_Id: newDesignData[0].Site_Id,
        design_Id: newDesignData[0].DesignID,
        buildingDesign: [],
    };
    // Iterate through buildings
    newDesignData[0].Buildings.forEach((building) => {
        const buildingInfo: BuildingInfo = {
            id: building.Id,
            attic_Access_Id: building.Attic_Access_Id,
            why_No_Attic_Access_Id: building.Why_No_Attic_Access_Id,
            attic_Access_Notes: building.Attic_Access_Notes,
            layers_of_Shingles_Id: building.Layers_of_Shingles_Id,
            all_Roof_The_Same: building.All_Roof_The_Same,
            number_Of_Stories: building.Number_Of_Stories ?? null,
            roofDesign: [],
        };

        // Iterate through roofs
        building.Roofs.forEach((roof) => {
            const roofMesh = sceneViewer.roofMeshArr.find((mesh: RoofMesh) => mesh.userData.roof_tag_id === Number(roof.Designation.replace(/\D/g, '')));
            const moduleCount = roofMesh?.children?.filter((c: any) => c.module).length;
            const RailManufacturer = masterDataList?.RailManufacturer.find((item) => item.value === Number(roof.Rail_Manufacturer))?.label ?? roof.Rail_Manufacturer;
            const AttachmentManufacturer = masterDataList?.AttachmentManufacturer.find((item) => item.value === Number(roof.Attachment_Manufacturer))?.label ?? roof.Attachment_Manufacturer;
            const activeInd = deactivatedRoofIdArr.find(r => r === roof.Id) ? false : roof.Active_Ind ?? true;

            const roofInfo: RoofInfo = {
                id: roof.Id ?? 0,
                rafter_Space_Id: roof.Rafter_Space_Id ?? 0,
                active_Ind: activeInd,

                // Default Roof Properties
                rafter_Size: roof.Rafter_Size ?? "",
                existing_Rafter_Qty: roof.Existing_Rafter_Qty ?? 0,
                unsupported_Rafter_Span: roof.Unsupported_Rafter_Span ? String(roof.Unsupported_Rafter_Span) : "",
                rafter_Spacing: roof.Rafter_Spacing ?? null,
                max_Span: roof.Max_Span ?? 0,
                roof_Type_Id: roof.Roof_Type_Id,
                structure_Type_Id: roof.Structure_Type_Id,
                roof_Condition_Id: roof.Roof_Condition_Id,
                include_In_Letter: roof.Include_In_Letter ?? true,
                psF_Id: roof.PSF_Id,
                notes: roof.Notes ?? "",
                dead_Load_Id: roof.Dead_Load_Id ?? null,
                roof_Has_Attic_Acces: roof.Roof_Has_Attic_Acces ?? null,
                // Additional Roof Properties
                existing_Solar_Panels: roof.Existing_Solar_Panels ?? "",
                existing_Solar_Area: roof.Existing_Solar_Panels === 'No' ? 0
                    : Number(roof.Existing_Solar_Area) ?? 0,
                pV_Module_Type: roof.PV_Module_Type ?? "",
                rail_Manufacturer: RailManufacturer ?? "",
                rail_Type: roof.Rail_Type ?? "",
                roof_Covering: roof.Roof_Covering ?? "",
                attachment_Manufacturer: AttachmentManufacturer ?? "",
                attachment_Type: roof.Attachment_Type ?? "",
                number_Of_Stories: roof.Number_Of_Stories ?? null,
                existing_Damage: roof.Existing_Damage ?? "",
                truss_Type: roof.Truss_Type ?? "",
                // From Design
                roof_Slope: roof.Roof_Slope ?? roofMesh?.oriAng.tilt ?? null,
                roof_Area: roof.Roof_Area ?? roofMesh.size ?? null,
                pV_Module_Count: roof.PV_Module_Count ?? moduleCount,
                // Quesionable Roof
                questionable_Roof: roof.Questionable_Roof ?? false,
                questionable_Roof_Id: roof.Questionable_Roof_Id ?? 0,
                questionable_Roof_Notes: roof.Questionable_Roof_Notes ?? "",
            };
            buildingInfo.roofDesign.push(roofInfo);
        });

        mappedData.buildingDesign.push(buildingInfo);
    });


    siteService
        .updateBuildingAttributes(mappedData)
        .then(async () => {
            sceneViewer.roofMeshArr.forEach((shapeMesh: RoofMesh) => {
                newDesignData[0].Buildings.forEach(building => {
                    building.Roofs.forEach(roofData => {
                        if (roofData.Designation === `R${shapeMesh.userData.roof_tag_id}`) {
                            const qnRoofId = roofData?.Questionable_Roof_Id ?? 0;
                            const roofNote = masterDataList?.QuestionableRoof.find(data => data.value === qnRoofId);
                            shapeMesh.userData.questionableRoof = roofNote?.label ?? '';
                            shapeMesh.userData.questionableRoofId = roofData?.Questionable_Roof_Id ?? 0;
                            shapeMesh.userData.questionableRoofNotes = roofData?.Questionable_Roof_Notes ?? '';
                            return;
                        }
                    });
                })
            })

            const newSiteDesignDetails = [...siteDesignDetails];
            newSiteDesignDetails[0].Design[0] = newDesignData[0];

            useViewerStore.setState({ 
                designList: newDesignData,
                siteDesignDetails: newSiteDesignDetails,
                siteBuildingData: newDesignData[0].Buildings,
                isRoofChange: false,
                refreshQuestionableRoof: !refreshQuestionableRoof,
                isBuildingChange: false,
            });
            callback?.(true);
            alertMessage("success", "The building data updated successfully");
        })
        .catch((error) => {
            alertMessage("error", "Error while updating building data");
            callback?.(false);
            logger.error(error);
        });
};